import React, { forwardRef } from "react";
import { cn } from "../../lib/utils.js";

/* ---------------- CountBadge ----------------
   A small count that OVERHANGS the inline-end top corner of a `relative` host
   control (a bell, a paperclip). It is decoration: the host's accessible name
   or description must carry the count, so the badge is hidden from assistive
   technology by default.

   Not a `Badge` variant: `Badge` is an inline label pill that sits in text flow;
   this is a positioned counter on another control.

   `tone` is the semantic colour (critical = needs attention, neutral = just a
   number); `ring` paints the surface the host sits on, so the badge reads as a
   separate object rather than a hole in the icon. */

export type CountBadgeTone = "critical" | "neutral";
export type CountBadgeRing = "shell" | "card";

const TONE: Record<CountBadgeTone, string> = {
  critical: "bg-pb-red text-pb-on-accent",
  neutral: "bg-pb-card-elevated text-pb-text-secondary",
};

const RING: Record<CountBadgeRing, string> = {
  shell: "ring-pb-shell",
  card: "ring-pb-card",
};

export interface CountBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  count: number;
  /** Counts above this read `${max}+`. */
  max?: number;
  tone?: CountBadgeTone;
  /** The surface behind the host control. */
  ring?: CountBadgeRing;
}

export const CountBadge = forwardRef<HTMLSpanElement, CountBadgeProps>(
  function CountBadge(
    { count, max = 99, tone = "neutral", ring = "card", className, ...rest },
    ref,
  ) {
    if (count <= 0) return null;
    return (
      <span
        ref={ref}
        data-count-badge
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute -top-1 -end-1 flex h-3.5 min-w-3.5 items-center justify-center rounded-full px-1 text-dense-3 font-semibold ring-2",
          TONE[tone],
          RING[ring],
          className,
        )}
        {...rest}
      >
        {count > max ? `${max}+` : count}
      </span>
    );
  },
);
